import { useState, useEffect, useCallback } from "react";
import { ArrowDownTrayIcon, ArrowPathRoundedSquareIcon, BellIcon, ChatBubbleLeftEllipsisIcon, ClipboardDocumentListIcon, CodeBracketIcon, DevicePhoneMobileIcon, EnvelopeIcon, KeyIcon, RectangleGroupIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, serverTimestamp, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { UserData } from "../types/user";
import { downloadUserExport, exportUserData } from "../services/userExportService";
import SendNotificationDialog from "./SendNotificationDialog";
import SendEmailDialog from "./SendEmailDialog";
import WearableDetailsDialog from "./WearableDetailsDialog";

interface AdminNote {
    id: string;
    note: string;
    createdAt: Timestamp;
    adminUid: string;
    adminEmail: string;
}

interface WeeklyUserReport {
    userId: string;
    userEmail: string;
    period: string;
    generatedAt: { seconds: number; nanoseconds: number };
    daysLogged: number;
    training: {
        weeklyTotal: number;
        wowChange: string;
        trend: "up" | "down" | "same";
    };
    wellness: {
        weeklyAverage: number;
        wowChange: string;
        trend: "up" | "down" | "same";
    };
    loadBalance: {
        score: number;
        insight: string;
    };
    mostImproved: {
        metric: string;
        wowChange: number;
    };
    leastImproved: {
        metric: string;
        wowChange: number;
    };
    trainingInsight: string;
    peakScore?: number;
    sendEmail?: boolean;
    emailSentAt?: any;
    _docId?: string;
    _dbName?: "version2" | "default";
}

interface UserDetailSidebarProps {
    user: (UserData & { uid: string }) | null;
    onClose: () => void;
    onSubscriptionClick?: (user: UserData & { uid: string }) => void;
    onGenerateReport?: (sendEmail: boolean) => void;
    generatingReport?: boolean;
    userReports?: WeeklyUserReport[];
    loadingReports?: boolean;
    onSelectReport?: (report: WeeklyUserReport) => void;
    formatReportTimestamp?: (ts: { seconds: number; nanoseconds: number } | undefined) => string;
}

const DetailRow: React.FC<{ label: string; value?: string | null; mono?: boolean; capitalize?: boolean }> = ({ label, value, mono, capitalize }) => (
    <div className="flex items-center gap-4 w-full">
        <div className="text-xs font-medium text-gray-500 uppercase">{label}</div>
        <div className={`text-sm break-words ${mono ? 'font-mono text-xs text-gray-400' : ''} ${capitalize ? 'capitalize' : ''}`}>
            {value}
        </div>
    </div>
);

const UserDetailSidebar: React.FC<UserDetailSidebarProps> = ({
    user,
    onClose,
    onSubscriptionClick,
    onGenerateReport,
    generatingReport,
    userReports,
    loadingReports,
    onSelectReport,
    formatReportTimestamp,
}) => {
    const { currentUser } = useAuth();
    const [showReportsModal, setShowReportsModal] = useState(false);
    const [showRawDataModal, setShowRawDataModal] = useState(false);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [sendEmail, setSendEmail] = useState(true);
    const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [wearableDialogOpen, setWearableDialogOpen] = useState(false);

    // Reset password state
    const [newPassword, setNewPassword] = useState("");
    const [resettingPassword, setResettingPassword] = useState(false);
    const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
    const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
    const [confirmedPassword, setConfirmedPassword] = useState<string | null>(null);
    const [copiedPassword, setCopiedPassword] = useState(false);

    // Notes state
    const [notes, setNotes] = useState<AdminNote[]>([]);
    const [loadingNotes, setLoadingNotes] = useState(false);
    const [newNote, setNewNote] = useState("");
    const [savingNote, setSavingNote] = useState(false);
    const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

    // Export state
    const [exportingData, setExportingData] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    const fetchNotes = useCallback(async (userId: string) => {
        setLoadingNotes(true);
        try {
            const notesRef = collection(db, "adminUserNotes", userId, "notes");
            const q = query(notesRef, orderBy("createdAt", "desc"));
            const snapshot = await getDocs(q);
            setNotes(snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as AdminNote)));
        } catch (error) {
            console.error("Error fetching notes:", error);
        } finally {
            setLoadingNotes(false);
        }
    }, []);

    // Fetch notes when modal opens
    useEffect(() => {
        if (showNotesModal && user) {
            fetchNotes(user.uid);
        }
    }, [showNotesModal, user, fetchNotes]);

    const handleAddNote = async () => {
        if (!newNote.trim() || !user || !currentUser) return;

        setSavingNote(true);
        try {
            const notesRef = collection(db, "adminUserNotes", user.uid, "notes");
            await addDoc(notesRef, {
                note: newNote.trim(),
                createdAt: serverTimestamp(),
                adminUid: currentUser.uid,
                adminEmail: currentUser.email || "",
            });
            setNewNote("");
            await fetchNotes(user.uid);
        } catch (error) {
            console.error("Error adding note:", error);
        } finally {
            setSavingNote(false);
        }
    };

    const handleDeleteNote = async (noteId: string) => {
        if (!user || !window.confirm("Delete this note? This cannot be undone.")) return;

        setDeletingNoteId(noteId);
        try {
            await deleteDoc(doc(db, "adminUserNotes", user.uid, "notes", noteId));
            setNotes((prev) => prev.filter((n) => n.id !== noteId));
        } catch (error) {
            console.error("Error deleting note:", error);
        } finally {
            setDeletingNoteId(null);
        }
    };

    const formatNoteTimestamp = (ts: Timestamp | undefined) => {
        if (!ts?.toDate) return "";
        const date = ts.toDate();
        return date.toLocaleDateString("en-US", {
            month: "short", day: "numeric", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    };

    const generateRandomPassword = () => {
        const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*";
        // Rejection sampling keeps every character equally likely.
        const limit = 256 - (256 % chars.length);
        let password = "";
        while (password.length < 16) {
            const bytes = crypto.getRandomValues(new Uint8Array(32));
            for (let i = 0; i < bytes.length && password.length < 16; i++) {
                if (bytes[i] < limit) password += chars[bytes[i] % chars.length];
            }
        }
        setNewPassword(password);
        setResetPasswordError(null);
        setResetPasswordSuccess(false);
    };

    const handleResetPassword = async () => {
        if (!user || !newPassword.trim()) return;
        if (newPassword.length < 6) {
            setResetPasswordError("Password must be at least 6 characters.");
            return;
        }

        setResettingPassword(true);
        setResetPasswordError(null);
        setResetPasswordSuccess(false);
        setConfirmedPassword(null);
        try {
            const adminResetUserPassword = httpsCallable<{ userId: string; password: string }, { success: boolean }>(
                functions,
                "adminResetUserPassword"
            );
            await adminResetUserPassword({ userId: user.uid, password: newPassword });
            setConfirmedPassword(newPassword);
            setResetPasswordSuccess(true);
            setNewPassword("");
        } catch (err: any) {
            setResetPasswordError(err?.message || "Failed to reset password.");
        } finally {
            setResettingPassword(false);
        }
    };

    const handleCopyPassword = () => {
        if (!confirmedPassword) return;
        navigator.clipboard.writeText(confirmedPassword);
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
    };

    const handleExportUserData = async () => {
        if (!user) return;
        setExportingData(true);
        setExportError(null);
        try {
            const data = await exportUserData(user.uid);
            downloadUserExport(data);
        } catch (error: any) {
            console.error("Error exporting user data:", error);
            setExportError(error?.message || "Failed to export user data.");
        } finally {
            setExportingData(false);
        }
    };

    return (
        <>
            <div
                className={`h-full bg-white border-l flex flex-col transition-all duration-300 overflow-hidden ${
                    user ? 'w-[400px] ml-6' : 'w-0 ml-0'
                }`}
            >
                {user && (
                    <>
                        {/* Header */}
                        <div className="flex items-start justify-between p-4">
                            <div className="flex items-center gap-4">
                                {user.imageUrl ? (
                                    <img
                                        src={user.imageUrl}
                                        alt={user.name || "User"}
                                        className="w-14 h-14 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-medium">
                                        {user.name?.charAt(0).toUpperCase() || "?"}
                                    </div>
                                )}
                                <div>
                                    <div className="font-medium font-grotesk">
                                        {user.name} {user.surname}
                                    </div>
                                    <div className="text-xs font-grotesk text-gray-500 lowercase">
                                        {user.username ? (user.username.startsWith("@") ? user.username : `@${user.username}`) : ""}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1 hover:bg-gray-100 rounded"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 border-t overflow-y-auto flex flex-col gap-6 p-4">
                            {/* User Details */}
                            <div className="flex flex-col gap-2">
                                <DetailRow label="UID" value={user.uid} mono/>
                                <div className="w-full border-t my-2"></div>
                                <DetailRow label="Subscription" value={user.subscription?.subscriptionType} capitalize/>
                                <DetailRow label="Via" value={user.subscription?.subscriptionVia} capitalize/>
                                {user.campaignCode && (
                                    <DetailRow label="Campaign Code" value={user.campaignCode} />
                                )}
                                <div className="w-full border-t my-2"></div>
                                <DetailRow label="Email" value={user.email} />
                                <DetailRow label="Gender" value={user.gender} />
                                <DetailRow label="Country" value={user.country} />
                                <DetailRow label="Date of Birth" value={user.dob} />
                                <div className="w-full border-t my-2"></div>
                                <DetailRow label="Role" value={user.role} />
                                <DetailRow label="Sport" value={user.sport} />
                                <div className="w-full border-t my-2"></div>
                                {user.organisationId && (
                                    <DetailRow label="Organisation ID" value={user.organisationId} />
                                )}
                                {user.orgIds && (
                                    <DetailRow label="No. of Coaching Groups" value={user.orgIds?.length?.toString()} />
                                )}
                                <div className="w-full border-t mt-2"></div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-col gap-1">
                                <a href={`https://peak.app/feed/?uid=${user.uid}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm !h-10 text-sm shadow-none relative">
                                    <RectangleGroupIcon className="size-5 absolute left-4 top-1/2 -translate-y-1/2" />
                                    View feed
                                </a>
                                <button
                                    className="btn btn-sm !h-10 text-sm shadow-none relative"
                                    onClick={() => setNotificationDialogOpen(true)}
                                >
                                    <BellIcon className="size-5 absolute left-4 top-1/2 -translate-y-1/2" />
                                    Send notification
                                </button>
                                <button
                                    className="btn btn-sm !h-10 text-sm shadow-none relative"
                                    onClick={() => setEmailDialogOpen(true)}
                                >
                                    <EnvelopeIcon className="size-5 absolute left-4 top-1/2 -translate-y-1/2" />
                                    Send email
                                </button>
                                <button
                                    className="btn btn-sm !h-10 text-sm shadow-none relative"
                                    onClick={() => setWearableDialogOpen(true)}
                                >
                                    <DevicePhoneMobileIcon className="size-5 absolute left-4 top-1/2 -translate-y-1/2" />
                                    Wearable devices
                                </button>
                                {onSubscriptionClick && (
                                    <button className="btn btn-sm !h-10 text-sm shadow-none relative" onClick={() => onSubscriptionClick(user)}>
                                        <ArrowPathRoundedSquareIcon className="size-5 absolute left-4 top-1/2 -translate-y-1/2" />
                                        Update subscription
                                    </button>
                                )}
                                {onGenerateReport && (
                                    <button
                                        className="btn btn-sm !h-10 text-sm shadow-none relative"
                                        onClick={() => setShowReportsModal(true)}
                                    >
                                        <ClipboardDocumentListIcon className="size-5 absolute left-4 top-1/2 -translate-y-1/2" />
                                        View athlete reports
                                    </button>
                                )}
                                <button
                                    className="btn btn-sm !h-10 text-sm shadow-none relative"
                                    onClick={() => setShowNotesModal(true)}
                                >
                                    <ChatBubbleLeftEllipsisIcon className="size-5 absolute left-4 top-1/2 -translate-y-1/2" />
                                    Admin notes
                                </button>
                                <button
                                    className="btn btn-sm !h-10 text-sm shadow-none relative"
                                    onClick={() => {
                                        setNewPassword("");
                                        setResetPasswordError(null);
                                        setResetPasswordSuccess(false);
                                        setConfirmedPassword(null);
                                        setCopiedPassword(false);
                                        setShowResetPasswordModal(true);
                                    }}
                                >
                                    <KeyIcon className="size-5 absolute left-4 top-1/2 -translate-y-1/2" />
                                    Reset password
                                </button>
                                <button
                                    className="btn btn-sm !h-10 text-sm shadow-none relative"
                                    onClick={() => setShowRawDataModal(true)}
                                >
                                    <CodeBracketIcon className="size-5 absolute left-4 top-1/2 -translate-y-1/2" />
                                    View raw data
                                </button>
                                <button
                                    className="btn btn-sm !h-10 text-sm shadow-none relative"
                                    onClick={handleExportUserData}
                                    disabled={exportingData}
                                >
                                    <ArrowDownTrayIcon className="size-5 absolute left-4 top-1/2 -translate-y-1/2" />
                                    {exportingData ? "Exporting..." : "Export user data"}
                                </button>
                                {exportError && (
                                    <p className="text-xs text-red-500 px-1">{exportError}</p>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Reports Modal */}
            {showReportsModal && user && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowReportsModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold">Weekly Reports</h2>
                                <p className="text-sm text-gray-500">{user.name} {user.surname}</p>
                            </div>
                            <button
                                onClick={() => setShowReportsModal(false)}
                                className="p-1 hover:bg-gray-100 rounded"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        {/* Generate Report */}
                        <div className="p-4 border-b bg-gray-50">
                            <div className="flex items-center justify-between gap-3">
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="checkbox"
                                        checked={sendEmail}
                                        onChange={(e) => setSendEmail(e.target.checked)}
                                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/50"
                                    />
                                    Send report to user
                                </label>
                                <button
                                    className="px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => onGenerateReport?.(sendEmail)}
                                    disabled={generatingReport}
                                >
                                    {generatingReport ? "Generating..." : "Generate Report"}
                                </button>
                            </div>
                        </div>

                        {/* Reports List */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {loadingReports ? (
                                <p className="text-sm text-gray-400 text-center py-8">Loading reports...</p>
                            ) : (userReports ?? []).length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-8">No reports found</p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {(userReports ?? []).map((report, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => {
                                                setShowReportsModal(false);
                                                onSelectReport?.(report);
                                            }}
                                            className="w-full text-left px-4 py-3 rounded-lg border hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="text-sm font-medium">{report.period}</div>
                                            <div className="text-xs text-gray-500">
                                                {formatReportTimestamp?.(report.generatedAt)}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Admin Notes Modal */}
            {showNotesModal && user && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowNotesModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold">Admin Notes</h2>
                                <p className="text-sm text-gray-500">{user.name} {user.surname}</p>
                            </div>
                            <button
                                onClick={() => setShowNotesModal(false)}
                                className="p-1 hover:bg-gray-100 rounded"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Add Note */}
                        <div className="p-4 border-b bg-gray-50">
                            <textarea
                                placeholder="Write a note..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                                rows={3}
                                value={newNote}
                                onChange={(e) => setNewNote(e.target.value)}
                            />
                            <div className="flex justify-end mt-2">
                                <button
                                    className="px-4 py-2 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={handleAddNote}
                                    disabled={savingNote || !newNote.trim()}
                                >
                                    {savingNote ? "Saving..." : "Add Note"}
                                </button>
                            </div>
                        </div>

                        {/* Notes List */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {loadingNotes ? (
                                <p className="text-sm text-gray-400 text-center py-8">Loading notes...</p>
                            ) : notes.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-8">No notes yet</p>
                            ) : (
                                <div className="flex flex-col gap-3">
                                    {notes.map((note) => (
                                        <div key={note.id} className="px-4 py-3 rounded-lg border bg-white">
                                            <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.note}</p>
                                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                                                <div className="text-xs text-gray-400">
                                                    <span className="font-medium text-gray-500">{note.adminEmail}</span>
                                                    {" - "}
                                                    {formatNoteTimestamp(note.createdAt)}
                                                </div>
                                                {note.adminUid === currentUser?.uid && (
                                                    <button
                                                        onClick={() => handleDeleteNote(note.id)}
                                                        disabled={deletingNoteId === note.id}
                                                        className="p-1 rounded hover:bg-red-100 text-red-500 transition-colors disabled:opacity-50"
                                                        title="Delete note"
                                                    >
                                                        <TrashIcon className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Password Modal */}
            {showResetPasswordModal && user && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowResetPasswordModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold">Reset Password</h2>
                                <p className="text-sm text-gray-500">{user.name} {user.surname}</p>
                            </div>
                            <button onClick={() => setShowResetPasswordModal(false)} className="p-1 hover:bg-gray-100 rounded">
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 flex flex-col gap-4">
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="New password"
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono"
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        setResetPasswordError(null);
                                        setResetPasswordSuccess(false);
                                    }}
                                />
                                <button
                                    className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    onClick={generateRandomPassword}
                                >
                                    Generate
                                </button>
                            </div>
                            {resetPasswordError && (
                                <p className="text-sm text-red-500">{resetPasswordError}</p>
                            )}
                            {resetPasswordSuccess && confirmedPassword && (
                                <div className="flex flex-col gap-1">
                                    <p className="text-xs font-medium text-green-600">Password reset successfully. Copy it below:</p>
                                    <button
                                        onClick={handleCopyPassword}
                                        className="flex items-center justify-between w-full px-3 py-2 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors group"
                                    >
                                        <span className="font-mono text-sm text-gray-800 select-all">{confirmedPassword}</span>
                                        <span className="text-xs text-green-700 font-medium ml-3 shrink-0">
                                            {copiedPassword ? "Copied!" : "Copy"}
                                        </span>
                                    </button>
                                </div>
                            )}
                            <button
                                className="w-full py-2 text-sm font-medium text-white bg-gray-700 rounded-lg hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleResetPassword}
                                disabled={resettingPassword || !newPassword.trim()}
                            >
                                {resettingPassword ? "Resetting..." : "Reset Password"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification Dialog */}
            {notificationDialogOpen && user && (
                <SendNotificationDialog
                    isOpen={notificationDialogOpen}
                    onClose={() => setNotificationDialogOpen(false)}
                    targetUser={{ id: user.uid, name: user.name || '', surname: user.surname || '', email: user.email || '' }}
                />
            )}

            {/* Email Dialog */}
            {emailDialogOpen && user && (
                <SendEmailDialog
                    isOpen={emailDialogOpen}
                    onClose={() => setEmailDialogOpen(false)}
                    targetUser={{ id: user.uid, name: user.name || '', surname: user.surname || '', email: user.email || '' }}
                />
            )}

            {/* Wearable Dialog */}
            {wearableDialogOpen && user && (
                <WearableDetailsDialog
                    isOpen={wearableDialogOpen}
                    onClose={() => setWearableDialogOpen(false)}
                    user={{ id: user.uid, name: user.name || '', surname: user.surname || '', email: user.email || '' }}
                />
            )}

            {/* Raw Data Modal */}
            {showRawDataModal && user && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRawDataModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b">
                            <div>
                                <h2 className="text-lg font-semibold">Raw Data</h2>
                                <p className="text-sm text-gray-500">{user.name} {user.surname}</p>
                            </div>
                            <button
                                onClick={() => setShowRawDataModal(false)}
                                className="p-1 hover:bg-gray-100 rounded"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm whitespace-pre-wrap">
                                {JSON.stringify(user, null, 2)}
                            </pre>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default UserDetailSidebar;
export type { WeeklyUserReport };
